import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { useEnvironment } from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { EnvironmentStatus_ContentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { type FC } from "react";
import { useParams } from "react-router-dom";
import { SquareArrowOutUpRight } from "lucide-react";
import { getRepoUrl } from "@/repo-url";
import { type PlainEnvironment } from "@/queries/environment-queries";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { ActionBar } from "@/components/environments/ActionBar";
import { useRunner } from "@/queries/runner-queries";
import { EnvironmentBanner } from "@/components/environments/EnvironmentBanner";

export const EnvironmentDetailsHeader: FC<{
    showActionBar: boolean;
}> = ({ showActionBar }) => {
    const { environmentId } = useParams();
    const { data: environment, error: environmentError } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const { data: project } = useProject(environment?.metadata?.projectId);

    const isBasedOnProject = environment?.metadata?.projectId;
    const ready: boolean = (!!environment && !!project) || (!!environment && !environment?.metadata?.projectId);

    const gitFailed =
        Boolean(environmentError) || environment?.status?.content?.phase === EnvironmentStatus_ContentPhase.FAILED;

    return (
        <div className="flex flex-col gap-6">
            <EnvironmentBanner environment={environment} runner={runner} />
            <div className="flex flex-wrap items-center justify-between gap-1">
                <div className="flex min-w-0 shrink flex-col" translate="no">
                    <SkeletonText ready={ready} className="w-52" size={"lg"}>
                        <Heading1 className="flex flex-row align-middle text-content-primary">
                            {environment?.metadata?.name ? (
                                <>{environment?.metadata?.name}</>
                            ) : isBasedOnProject ? (
                                <>{project?.metadata?.name}</>
                            ) : (
                                <Repository environment={environment} failed={gitFailed} showRepoUrlLink={true} />
                            )}
                        </Heading1>
                    </SkeletonText>
                </div>
                <div className="min-w-fit">
                    {environmentId && showActionBar && <ActionBar environmentId={environmentId} />}
                </div>
            </div>
        </div>
    );
};

export const Repository: FC<{ failed: boolean; environment?: PlainEnvironment; showRepoUrlLink?: boolean }> = ({
    failed,
    environment,
    showRepoUrlLink = true,
}) => {
    const parsed = environment && getRepoUrl(environment);

    const { data: integrations } = useListRunnerSCMIntegrations(environment?.metadata?.runnerId);
    const scmId =
        parsed?.repoUrl && integrations
            ? integrations.find((integration) => new URL(parsed.repoUrl).host === integration.host)?.scmId
            : undefined;

    return (
        <div className="flex min-h-5 flex-row items-center justify-start gap-2 text-lg font-bold">
            <SkeletonText size="lg" className="w-40" failed={failed} ready={Boolean(environment)} hideOnFailed={true}>
                <SourceControlProviderIcon scmId={scmId} className="inline-block" size="base" />
                <div>{parsed ? `${parsed.account}/${parsed.repo}` : "From scratch"}</div>
                {showRepoUrlLink && parsed?.repoUrl && (
                    <a href={parsed.repoUrl} target="_blank" rel="noreferrer">
                        <SquareArrowOutUpRight size={14} className="text-content-secondary" />
                    </a>
                )}
            </SkeletonText>
        </div>
    );
};
